import { GPS_INSTANCE_CONFIG } from "../config";
import { formatIsoDate, getDB } from "../utils";
import { api, ApiError } from "../api/client";

const LOCATION_STORE = "location_context";

function buildLocationContextError(code, message, details = null) {
  const error = new Error(message);
  error.code = code;
  error.details = details;
  return error;
}

function withGpsInstanceConfig(context = {}) {
  return {
    ...context,
    gps_accuracy_max_raw: GPS_INSTANCE_CONFIG.gps_accuracy_max_raw,
    gps_accuracy_max: GPS_INSTANCE_CONFIG.gps_accuracy_max,
    gps_accuracy_max_configured: GPS_INSTANCE_CONFIG.gps_accuracy_max_configured,
    gps_accuracy_max_valid: GPS_INSTANCE_CONFIG.gps_accuracy_max_valid,
  };
}

function normalizeMode(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.toUpperCase() : null;
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeLocationContext(user, payload = {}, { dataOra, fetchedAt } = {}) {
  if (!user?.id || !user?.assunzione_id) {
    throw new Error("Utente non valido per il contesto location");
  }

  const location = payload.location_pianificata || {};
  const assunzione = payload.assunzione || {};
  const timestamp = fetchedAt || new Date().toISOString();
  const requestTime = dataOra || formatIsoDate(new Date(), true);

  return withGpsInstanceConfig({
    id: `${user.id}_${user.assunzione_id}`,
    user_id: user.id,
    assunzione_id: assunzione.assunzione_id || user.assunzione_id,
    ws_success: Boolean(payload.success),
    versione_ws: payload.versioneWS || null,
    location_id: location.id ?? null,
    gruppo_id: location.id ?? null,
    codice: location.codice || null,
    descrizione: location.descrizione || null,
    localizzazione: Boolean(location.localizzazione),
    mode_localizzazione: normalizeMode(location.mode_localizzazione),
    latitudine: toNumberOrNull(location.latitudine),
    longitudine: toNumberOrNull(location.longitudine),
    tolleranza: toNumberOrNull(location.tolleranza),
    data_ora_richiesta: requestTime,
    fetched_at: timestamp,
  });
}

export async function saveLocationContext(context) {
  if (!context) return null;
  const db = await getDB();
  await db.put(LOCATION_STORE, context);
  return context;
}

export async function getCachedLocationContext(userOrUserId, assunzioneId) {
  const userId = typeof userOrUserId === "object" ? userOrUserId?.id : userOrUserId;
  const resolvedAssunzioneId = typeof userOrUserId === "object"
    ? userOrUserId?.assunzione_id
    : assunzioneId;
  if (!userId || !resolvedAssunzioneId) return null;
  const db = await getDB();
  const cached = await db.get(LOCATION_STORE, `${userId}_${resolvedAssunzioneId}`);
  return cached ? withGpsInstanceConfig(cached) : null;
}

export async function fetchLocationContext(user, { dataOra } = {}) {
  if (!user?.assunzione_id || !user?.token) {
    throw buildLocationContextError(
      "GPS_CONTEXT_INVALID_USER_PARAMS",
      "Parametri utente insufficienti per recuperare la location pianificata",
      {
        user_id: user?.id || null,
        assunzione_id: user?.assunzione_id || null,
        has_token: Boolean(user?.token),
      }
    );
  }

  const payload = {
    assunzione_id: user.assunzione_id,
    session_token: user.token,
  };

  if (dataOra) {
    payload.data_ora = dataOra;
  }

  let data;
  try {
    data = await api.post("/api/timbrature/location_pianificata", payload);
  } catch (err) {
    if (err instanceof ApiError && !err.isNetworkError) {
      const errBody = err.body && typeof err.body === "object" ? err.body : null;
      throw buildLocationContextError(
        "GPS_CONTEXT_WS_ERROR",
        errBody?.details?.error || errBody?.details?.message || errBody?.message || errBody?.error || err.message || "Impossibile recuperare la location pianificata",
        errBody?.details || errBody || null
      );
    }
    throw buildLocationContextError(
      "GPS_CONTEXT_INVALID_RESPONSE",
      "Risposta non valida durante il recupero della location pianificata",
      { cause: err?.message || String(err) }
    );
  }

  if (data?.success === false) {
    throw buildLocationContextError(
      "GPS_CONTEXT_WS_ERROR",
      data?.details?.error || data?.details?.message || data?.message || data?.error || "Impossibile recuperare la location pianificata",
      data?.details || data || null
    );
  }

  if (!data?.location_pianificata) {
    throw buildLocationContextError(
      "GPS_CONTEXT_WS_MISSING_LOCATION",
      "Cedolino non ha restituito una location pianificata per questa assunzione.",
      {
        assunzione_id: user.assunzione_id,
        ws_success: data?.success === true,
        has_assunzione: Boolean(data?.assunzione),
      }
    );
  }

  return normalizeLocationContext(user, data, {
    dataOra: payload.data_ora || formatIsoDate(new Date(), true),
    fetchedAt: new Date().toISOString(),
  });
}

export async function fetchAndCacheLocationContext(user, options = {}) {
  const fresh = await fetchLocationContext(user, options);
  await saveLocationContext(fresh);
  return fresh;
}